import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import {
	Modal,
	Button,
	ProgressIndicator,
	noop
} from "rhombus-react";

import {
	setVASTUploadList,
	uploadVASTAudioCreative,
	encodeVASTAudioCreatives,
	encodeVastAudioGrowls
} from "reducers/creatives/VAST";
import { addNotification } from "reducers/Notifications";
import { loadCreatives } from "reducers/Creatives";

import EmptySection from "widgets/EmptySection";
import SelectAssets from "widgets/modals/assets/SelectAssets";

import useCancelToken from "hooks/useCancelToken";

import { useModalStack, formatBytes, getUploadFiles } from "utils/common";
import { UPLOAD_AUDIO_ASSET_MAX_BYTES, UPLOAD_AUDIO_ACCEPTED_FORMATS, UPLOAD_AUDIO_CREATIVE } from "utils/constants";

import VASTUploadList from "./VASTUploadList";
import SkipModal from "./SkipModal";

import './CreateAudio.css';

export default function CreateAudio(props) {
	const { returnPath, libraryId, creativeId, isMultiple } = props;
	const dispatch = useDispatch();
	let modalTitle = "Create Audio";

	if (creativeId) {
		modalTitle = "Select New Audio";
	}

	const commonModalProps = {
		title: modalTitle,
		type: "modal",
		className: "create-audio-modal",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		onClose: closeModal,
		setAppTitle: false,
	};

	const [, { pushModal }, ModalStackHead] = useModalStack({ commonModalProps });

	const { isLoaded } = useSelector(state => ({
		isLoaded: state.libraryProperties?.isLoaded,
	}));

	const { token: cancelToken, cancel } = useCancelToken();
	const [isUploading, setIsUploading] = useState(false);
	let replaceCreative = false;

	const formattedMaxSize = formatBytes(UPLOAD_AUDIO_ASSET_MAX_BYTES);

	function closeModal() {
		cancel("Uploads canceled.");
		dispatch(push(returnPath));
	}

	if (!isMultiple && creativeId) {
		replaceCreative = { id: creativeId };
	}

	function openSelect(){
		function Configured(props){
			let modalProps = {
				...props,
				type: "audio",
				multiple: isMultiple,
				submitButtonText: "Create Audio Creative",
				submitButtonTheme: "green",

				onSelect: (selected) => {
					return dispatch(encodeVASTAudioCreatives(libraryId, selected, replaceCreative))
						.then(closeModal);
				},
				accept: UPLOAD_AUDIO_ACCEPTED_FORMATS,
			};

			return <SelectAssets {...modalProps} />;
		}

		pushModal(Configured);
	}

	function openSkip(file, message, onSkip = noop) {
		function Configured(props){
			let modalProps = {
				...props,

				file,
				message,
				onSkip,
			};
			return <SkipModal {...modalProps} />;
		}

		pushModal(Configured);
	}


	function onUpload(e) {
		let fileList = e.target.files;

		dispatch(setVASTUploadList(getUploadFiles(fileList)));
		setIsUploading(true);
		let numSuccessful = 0;

		function uploadFile(itemNum){
			let file = fileList.item(itemNum);

			if (!file) {
				if(numSuccessful > 0) {
					dispatch(addNotification({
						message: encodeVastAudioGrowls(numSuccessful),
						type: "success",
					}));
					dispatch(loadCreatives(libraryId));
				}
				closeModal();
				return;
			}

			if (file.size > UPLOAD_AUDIO_ASSET_MAX_BYTES) {
				let description = `Cannot upload file “${file.name}” because it exceeds the maximum file size of ${formattedMaxSize}.`;
				openSkip(file, description, () => { uploadFile(itemNum + 1); });
				return;
			}

			dispatch(uploadVASTAudioCreative(libraryId, file, cancelToken, replaceCreative))
				.then((resp) => {
					if (resp.isCanceled) {
						closeModal();
						return;
					}
					if (resp.success) {
						/*
							The response is "successful" even when errors are sent back for a file upload.
							We are submitting uploads sequentially, so errors will always be in the 0 index
							as opposed to matching `itemNum`.

							However, even with an error, we only want to skip the creative if it does not exist
							in the creatives payload.
						*/
						const { creatives = [], errors = []} = resp.payload?.data || {};

						if (!creatives.length && errors.length) {
							const [errorObj] = errors;
							const description = errorObj.errors.map(errorObj => errorObj.error).join('');
							openSkip(file, description, () => { uploadFile(itemNum + 1); });
							return;
						}

						numSuccessful += 1;
						uploadFile(itemNum + 1);
					} else {
						let { description = `Cannot upload file ${file.name}` } = (resp?.payload?.errorObj || {});
						openSkip(file, description, () => { uploadFile(itemNum + 1); });
					}
				});
		}

		uploadFile(0);
	}

	function Body() {
		if (isUploading) {
			return <VASTUploadList />;
		}

		return (
			<div className="body-container">
				<ProgressIndicator key="loading" isLoading={!isLoaded} />
				<header className="header-info-audio">
					<div className="text-container">
						<strong>Select how you would like to add audio</strong>
						<p>Accepted file types: {UPLOAD_AUDIO_ACCEPTED_FORMATS}</p>
					</div>
				</header>
				<div className="selection-routes">
					<label htmlFor={UPLOAD_AUDIO_CREATIVE} className="upload-audio">
						<EmptySection
							icon="fal fa-cloud-upload"
							header="Upload Audio"
							message={`(${formattedMaxSize} max file size per audio)`}
						/>
					</label>

					<div className="open-select" onClick={openSelect}>
						<EmptySection
							className="choose-existing"
							icon="fal fa-folder-open"
							header="Choose Existing Audio"
						/>
					</div>
				</div>
				<input
					type="file"
					multiple={isMultiple}
					id={UPLOAD_AUDIO_CREATIVE}
					className="hidden"
					accept={UPLOAD_AUDIO_ACCEPTED_FORMATS}
					onChange={onUpload}
				/>
			</div>
		);
	}

	function Footer() {
		return (
			<Button
				theme="default"
				type="reset"
				onClick={closeModal}
			>
				Cancel
			</Button>
		);
	}

	if (ModalStackHead) {
		return <ModalStackHead />;
	}

	return (
		<Modal
			{...commonModalProps}
			renderContent={Body}
			renderFooter={Footer}
		/>
	);
}

CreateAudio.displayName = "CreateAudio";
CreateAudio.defaultProps = {
	libraryId: -1,
	creativeId: null,
	returnPath: "",
};
